import React from 'react'
import { Box, Button, Card, CardContent, Stack, TextField, Typography, Divider, Chip } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { getShowClockDayAudit, type ShowClockAuditEvent } from '../../api/showClockAudit'
import { formatClockTimeLabel } from '../../utils/showClock'
import useStationStore from '../../store/station'
import { usePageTitle } from '../../components/common/PageTitleContext'
import { getErrorMessage } from '../../utils/errors'

const formatDetails = (details: unknown): string => {
  if (details === null || details === undefined) {
    return ''
  }
  if (typeof details === 'string') {
    return details
  }
  try {
    return JSON.stringify(details)
  } catch (error) {
    return ''
  }
}

const ShowClockAuditPage: React.FC = () => {
  const activeStation = useStationStore((state) => state.activeStation)
  usePageTitle('Show Clock Audit', { align: 'left', showBreadcrumbs: false })
  const [dayId, setDayId] = React.useState('')
  const [limit, setLimit] = React.useState('50')
  const [cursor, setCursor] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [events, setEvents] = React.useState<ShowClockAuditEvent[]>([])
  const [nextCursor, setNextCursor] = React.useState<string | null>(null)

  const eventsCsv = React.useMemo(() => {
    if (events.length === 0) {
      return ''
    }
    const headers = ['id', 'day_id', 'item_id', 'user_id', 'action', 'ts_epoch', 'created_at', 'details']
    const rows = events.map((evt) => {
      const detailsStr = formatDetails(evt.details).replace(/"/g, '""')
      return [
        evt.id,
        evt.day_id,
        evt.item_id ?? '',
        evt.user_id ?? '',
        evt.action,
        evt.ts_epoch,
        evt.created_at ?? '',
        detailsStr,
      ]
        .map((col) => `"${String(col)}"`)
        .join(',')
    })
    return [headers.join(','), ...rows].join('\n')
  }, [events])

  const handleFetch = React.useCallback(
    async (resetCursor: boolean) => {
            const stationId = activeStation?.id
      if (!stationId) {
        setError('Select a station to view audit logs.')
        return
      }
      const day = parseInt(dayId, 10)
      if (Number.isNaN(day) || day <= 0) {
        setError('Enter a valid day id.')
        return
      }
      const limitValue = parseInt(limit, 10)
      setLoading(true)
      setError(null)
      try {
        const response = await getShowClockDayAudit(
          {
            stationId,
            dayId: day,
            limit: Number.isNaN(limitValue) ? undefined : limitValue,
            cursor: resetCursor ? null : cursor,
          },
          { signal: undefined },
        )
        setEvents(response.events)
        setNextCursor(response.next_cursor)
        if (resetCursor) {
          setCursor(null)
        }
      } catch (err) {
        setError(getErrorMessage(err, 'Unable to load audit events.'))
      } finally {
        setLoading(false)
      }
    },
    [activeStation?.id, dayId, limit, cursor],
  )

  const handleLoadMore = React.useCallback(() => {
    if (!nextCursor) return
    setCursor(nextCursor)
    void handleFetch(false)
  }, [handleFetch, nextCursor])

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Card>
        <CardContent>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="flex-end">
            <TextField
              label="Day ID"
              value={dayId}
              onChange={(e) => setDayId(e.target.value)}
              type="number"
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label="Limit"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              type="number"
              inputProps={{ min: 1, max: 200 }}
              sx={{ width: { xs: '100%', sm: 120 } }}
            />
            <Button variant="contained" onClick={() => void handleFetch(true)} disabled={loading}>
              {loading ? 'Loading�' : 'Fetch audit'}
            </Button>
          </Stack>
          {error ? (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <Stack spacing={2}>
          {events.map((event) => (
            <Card key={event.id} variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle1">{event.action}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {`Event ID ${event.id}`}
                    </Typography>
                  </Box>
                  <Chip size="small" label={formatClockTimeLabel(event.ts_epoch)} />
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Day: {event.day_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Item: {event.item_id ?? '�'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    User: {event.user_id ?? '�'}
                  </Typography>
                </Stack>
                {event.details ? (
                  <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {formatDetails(event.details)}
                  </Typography>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          {nextCursor ? (
            <Button variant="outlined" onClick={handleLoadMore} disabled={loading}>
              Load more
            </Button>
          ) : null}
          {events.length > 0 ? (
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() => {
                const blob = new Blob([eventsCsv], { type: 'text/csv;charset=utf-8;' })
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `showclock-audit-day-${dayId || 'unknown'}.csv`
                link.click()
                URL.revokeObjectURL(url)
              }}
            >
              Export CSV
            </Button>
          ) : null}
        </Stack>
        {!loading && events.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No audit events yet.
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

export default ShowClockAuditPage

